import {
  LEARN_FILMS,
  LEARN_RETIRED_VIDEOS,
  WORKSHEET_CATEGORY_VALUES,
  WORKSHEET_FIELD_TYPE_VALUES,
  WORKSHEET_LANGUAGE_VALUES,
  worksheetVideoSourceOf,
} from '@cc/schema';

/**
 * E34 · Worksheets on the portal (block 2a) — what the library, the Builder and the backoffice's
 * publish screen share: the fields each read asks for, the DRAFT the Builder edits, and small PURE
 * helpers (a draft ↔ the wire, fresh ids, the video's embed address). No i18n here: helpers return
 * KEYS or data, the screens translate.
 *
 * THE DRAFT is the board's template schema (sections → fields; scoring; the signature and repeat
 * switches) with the scoring bands as objects (the wire's shape — the API turns them into the board's
 * `[min, max, label]` tuples). Ids are minted HERE, never typed: sections `s1, s2…`, fields `f1, f2…`,
 * the next free number, so a duplicate id (WORKSHEET_DUPLICATE_FIELD) cannot come from this editor.
 */

export const WORKSHEET_CATEGORIES = WORKSHEET_CATEGORY_VALUES;
export type WorksheetCategory = (typeof WORKSHEET_CATEGORIES)[number];
/**
 * F-197 (Q-lib-cat-2 A, 2026-09-30): `homework` stays a VALID category (events already carry it) but
 * nothing new is filed under it — the pickers offer the purpose categories, `journal`, `intake`,
 * `consent` and `lesson`. A template that already has `homework` keeps showing it (`categoryChoices`).
 */
export const PICKABLE_CATEGORIES: readonly WorksheetCategory[] = WORKSHEET_CATEGORIES.filter(
  (c) => c !== 'homework',
);
export const categoryChoices = (current?: string | null): readonly WorksheetCategory[] =>
  current === 'homework' ? WORKSHEET_CATEGORIES : PICKABLE_CATEGORIES;
export const FIELD_TYPES = WORKSHEET_FIELD_TYPE_VALUES;
export type FieldType = (typeof FIELD_TYPES)[number];
export const LANGUAGES = WORKSHEET_LANGUAGE_VALUES;
export type WorksheetLanguage = (typeof LANGUAGES)[number];

/**
 * The `language` the Worksheets page sends (the founder's ruling, 2026-10-02): by default the portal's own
 * language — the practice's worksheets and the library, the whole list, only that language; with "Show
 * worksheets in all languages" ticked, the dropdown's pick (null = every language). A portal language the
 * templates do not come in sends null (nothing is hidden by a guess).
 */
export const worksheetLanguageArg = (
  showAll: boolean,
  pick: string | undefined,
  portalLanguage: string,
): string | null => {
  if (showAll) return pick ?? null;
  return (LANGUAGES as readonly string[]).includes(portalLanguage) ? portalLanguage : null;
};

export const CHOICE_TYPES: readonly FieldType[] = ['singleChoice', 'multiChoice'];
export const NUMERIC_TYPES: readonly FieldType[] = ['number', 'scale'];

/** The library's row (WorksheetTemplates). */
export const TEMPLATE_ROW_FIELDS = [
  'templateId',
  'practiceId',
  'version',
  'name',
  'category',
  'language',
  'signatureRequired',
  'repeatable',
  'owner',
  'assignedCount',
  'copiedFrom',
  'fieldCount',
  'paperEligible', // Q-paper-2 A: unscored and not a consent — Print is offered, the client may complete on paper
  'occurredAt',
];

export const SECTION_FIELDS = [
  'sectionId',
  'title',
  'text',
  { video: ['source', 'ref', 'title', 'duration'] },
  { fields: ['fieldId', 'type', 'label', 'required', 'options', 'min', 'max'] },
];

/** The Builder's load (a template's latest version, whole). */
export const TEMPLATE_DETAIL_FIELDS = [
  ...TEMPLATE_ROW_FIELDS.filter((f) => f !== 'assignedCount'),
  'retired',
  { sections: SECTION_FIELDS },
  { scoring: ['kind', 'fields', { bands: ['min', 'max', 'label'] }] },
];

export const TEMPLATE_PAYLOAD_FIELDS = ['templateId', 'version', 'recorded'];

export type TemplateRow = {
  templateId: string;
  practiceId: string | null;
  version: number;
  name: string;
  category: WorksheetCategory;
  language: string;
  signatureRequired: boolean;
  repeatable: boolean;
  owner: 'practice' | 'library';
  assignedCount: number;
  copiedFrom: string | null;
  fieldCount: number;
  /** Q-paper-2 A — unscored and not a consent: the sheet can be printed and done on paper (the API computes it). */
  paperEligible: boolean;
  occurredAt: string;
};

export type DraftVideo = {
  source: 'learn' | 'youtube' | 'vimeo';
  ref: string;
  title: string;
  duration?: number | null;
};

export type DraftField = {
  fieldId: string;
  type: FieldType;
  label: string;
  required: boolean;
  options: string[];
  min: number | null;
  max: number | null;
};

export type DraftSection = {
  sectionId: string;
  title: string;
  text: string;
  video: DraftVideo | null;
  fields: DraftField[];
};

export type DraftBand = { min: number; max: number; label: string };

export type WorksheetDraft = {
  name: string;
  category: WorksheetCategory;
  language: WorksheetLanguage;
  sections: DraftSection[];
  scoring: { enabled: boolean; fields: string[]; bands: DraftBand[] };
  signatureRequired: boolean;
  repeatable: boolean;
  copiedFrom: string | null;
};

export type TemplateDetail = Omit<TemplateRow, 'assignedCount'> & {
  retired: boolean;
  sections: Array<{
    sectionId: string;
    title?: string | null;
    text?: string | null;
    video?: DraftVideo | null;
    fields: Array<{
      fieldId: string;
      type: FieldType;
      label: string;
      required?: boolean | null;
      options?: string[] | null;
      min?: number | null;
      max?: number | null;
    }>;
  }>;
  scoring?: { kind: string; fields: string[]; bands: DraftBand[] } | null;
};

const nextId = (prefix: string, taken: string[]): string => {
  let n = 1;
  while (taken.includes(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
};

export const allFieldIds = (d: Pick<WorksheetDraft, 'sections'>): string[] =>
  d.sections.flatMap((s) => s.fields.map((f) => f.fieldId));

export const newField = (
  type: FieldType,
  d: Pick<WorksheetDraft, 'sections'>,
): DraftField => ({
  fieldId: nextId('f', allFieldIds(d)),
  type,
  label: '',
  required: false,
  options: CHOICE_TYPES.includes(type) ? ['', ''] : [],
  min: type === 'scale' ? 0 : null,
  max: type === 'scale' ? 10 : null,
});

export const newSection = (d: Pick<WorksheetDraft, 'sections'>): DraftSection => ({
  sectionId: nextId(
    's',
    d.sections.map((s) => s.sectionId),
  ),
  title: '',
  text: '',
  video: null,
  fields: [],
});

export const emptyDraft = (language: WorksheetLanguage = 'en'): WorksheetDraft => {
  const d: WorksheetDraft = {
    name: '',
    category: 'sessions',
    language,
    sections: [],
    scoring: { enabled: false, fields: [], bands: [] },
    signatureRequired: false,
    repeatable: false,
    copiedFrom: null,
  };
  return { ...d, sections: [newSection(d)] };
};

/** A template's latest version as a draft — to edit (its own) or to copy ("Use this template"). */
export const draftFromDetail = (
  t: TemplateDetail,
  mode: 'edit' | 'copy',
): WorksheetDraft => ({
  name: t.name,
  category: t.category,
  language: (LANGUAGES as readonly string[]).includes(t.language)
    ? (t.language as WorksheetLanguage)
    : 'en',
  sections: (t.sections ?? []).map((s) => ({
    sectionId: s.sectionId,
    title: s.title ?? '',
    text: s.text ?? '',
    video: s.video
      ? {
          source: s.video.source,
          ref: s.video.ref,
          title: s.video.title,
          duration: s.video.duration ?? null,
        }
      : null,
    fields: (s.fields ?? []).map((f) => ({
      fieldId: f.fieldId,
      type: f.type,
      label: f.label,
      required: !!f.required,
      options: f.options ?? [],
      min: f.min ?? null,
      max: f.max ?? null,
    })),
  })),
  scoring: t.scoring
    ? { enabled: true, fields: t.scoring.fields ?? [], bands: t.scoring.bands ?? [] }
    : { enabled: false, fields: [], bands: [] },
  signatureRequired: t.signatureRequired,
  repeatable: t.repeatable,
  // a copy remembers where it came from; an edit keeps the template's own provenance server-side
  copiedFrom: mode === 'copy' ? t.templateId : t.copiedFrom ?? null,
});

const clean = (v: string): string | null => (v.trim() ? v.trim() : null);

/** The draft as the wire takes it (Publish / Define / Revise content). */
export const contentOfDraft = (d: WorksheetDraft) => ({
  name: d.name.trim(),
  category: d.category,
  language: d.language,
  sections: d.sections.map((s) => ({
    sectionId: s.sectionId,
    title: clean(s.title),
    text: clean(s.text),
    video: s.video
      ? {
          source: s.video.source,
          ref: s.video.ref.trim(),
          title: s.video.title.trim(),
          ...(s.video.duration ? { duration: s.video.duration } : {}),
        }
      : null,
    fields: s.fields.map((f) => ({
      fieldId: f.fieldId,
      type: f.type,
      label: f.label.trim(),
      required: f.required,
      ...(CHOICE_TYPES.includes(f.type)
        ? { options: f.options.map((o) => o.trim()).filter(Boolean) }
        : {}),
      ...(NUMERIC_TYPES.includes(f.type) && f.min !== null ? { min: f.min } : {}),
      ...(NUMERIC_TYPES.includes(f.type) && f.max !== null ? { max: f.max } : {}),
    })),
  })),
  scoring: d.scoring.enabled
    ? { kind: 'sum', fields: d.scoring.fields, bands: d.scoring.bands }
    : null,
  signatureRequired: d.signatureRequired,
  repeatable: d.repeatable,
});

/** The fields a scoring rule may sum — number and scale questions. */
export const numericFields = (d: Pick<WorksheetDraft, 'sections'>) =>
  d.sections.flatMap((s) => s.fields).filter((f) => NUMERIC_TYPES.includes(f.type));

/** Move one item of a list (the drag or the keyboard reorder). */
export const moveItem = <T>(list: T[], from: number, to: number): T[] => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length)
    return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

/**
 * The CopingCard Learn videos of a language the Builder's and the Assign drawer's pickers offer — E56 (founder Q-learn-7):
 * ONLY the new films that have a YouTube id (English: the ten; Russian: Dmitri's seven, then the generated ones). The
 * seven old English clips are not offered; a worksheet or homework that already names one still plays it
 * (`LEARN_VIDEO_IDS` accepts it on save, the phone's retired lookup names it).
 */
export const learnVideosOf = (
  language: string,
): Array<{ id: string; name: string }> => {
  const lang = language === 'ru' ? 'ru' : 'en';
  return LEARN_FILMS.flatMap((film) => {
    const v = film[lang];
    return v && v.id ? [{ id: v.id, name: v.name }] : [];
  });
};

/**
 * The picker's options: the new films, plus — only when the field ALREADY holds one of the seven retired clips —
 * that clip with its old title, so an existing worksheet or assignment still shows what it plays instead of a bare
 * YouTube id (Q-learn-7 A). A retired clip is never offered to a field that does not hold it.
 */
export const learnVideoOptionsOf = (
  language: string,
  current?: string | null,
): Array<{ id: string; name: string }> => {
  const options = learnVideosOf(language);
  const kept = current ? LEARN_RETIRED_VIDEOS.find((v) => v.id === current) : undefined;
  return kept && !options.some((o) => o.id === kept.id) ? [kept, ...options] : options;
};

/** The source of a pasted link (youtube · vimeo) or null — the video card's auto-detection. */
export const linkSourceOf = worksheetVideoSourceOf;

const YT_ID = /(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{6,20})/;
const VIMEO_ID = /vimeo\.com\/(?:video\/)?(\d+)(?:\/([0-9a-f]+))?/;

/**
 * Where the preview plays a section's video — a Learn video and a YouTube link in YouTube's embed (the
 * app plays both in E15's YouTube player), a Vimeo link in Vimeo's own (an unlisted link's hash kept).
 * Null when the link names no video.
 */
export const embedUrlOf = (v: DraftVideo | null | undefined): string | null => {
  if (!v?.ref) return null;
  if (v.source === 'learn') return `https://www.youtube-nocookie.com/embed/${v.ref}`;
  if (v.source === 'youtube') {
    const m = YT_ID.exec(v.ref);
    return m ? `https://www.youtube-nocookie.com/embed/${m[1]}` : null;
  }
  const m = VIMEO_ID.exec(v.ref);
  return m ? `https://player.vimeo.com/video/${m[1]}${m[2] ? `?h=${m[2]}` : ''}` : null;
};
